"use client";

import * as React from "react";

export type PermissionContextValue = {
  permissions: Set<string> | string[];
  has: (permission: string) => boolean;
};

const PermissionContext = React.createContext<PermissionContextValue | null>(
  null,
);

export function PermissionProvider({
  permissions,
  children,
}: {
  permissions: string[];
  children: React.ReactNode;
}) {
  const value = React.useMemo<PermissionContextValue>(() => {
    const set = new Set(permissions);
    return {
      permissions: set,
      has: (permission: string) => set.has(permission) || set.has("*"),
    };
  }, [permissions]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const ctx = React.useContext(PermissionContext);
  if (!ctx) {
    return {
      permissions: new Set<string>(),
      has: () => false,
    } satisfies PermissionContextValue;
  }
  return ctx;
}

/**
 * PermissionGate — hides children when the current actor lacks the permission.
 * Contrast with EntitlementGate (greys out / contact-admin). Do not merge behaviors.
 *
 * Pass either `permission` (single) or `anyOf` (OR). If both are set, either match allows.
 */
export function PermissionGate({
  permission,
  anyOf,
  children,
  fallback = null,
}: {
  permission?: string;
  anyOf?: readonly string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { has } = usePermissions();
  const allowed =
    (permission ? has(permission) : false) ||
    (anyOf?.some((code) => has(code)) ?? false);
  if (!allowed) return <>{fallback}</>;
  return <>{children}</>;
}
